import { useState, useEffect, useCallback, useRef } from 'react';
import SignRouteMap from './SignRouteMap';
import { MobilePage, BottomSheet, StickyAction, Icon, useIsMobile } from '../../mobile';

// ============================================================
// Field route page — the sign tech's and tagger's day, phone-first.
// One job per screen: get to the next stop and report what happened.
// Layout rules (Sep 8 mobile rebuild):
//   - large title, progress at a glance, date chips only when needed
//   - the active stop is expanded; every other stop is one line
//   - "Update Work Status" is PINNED above the tab bar and never moves
//   - the outcome flow is a bottom sheet with its submit in a fixed footer
// Both routes share this component; the config carries the differences.
// Sign tasks (Oct 1): non-job work the manager adds to the day. With an
// address a task is a numbered stop in the list; without one it's shop
// work listed on its own. Either way the tech taps Done (optional note +
// photo) — no outcome survey. Routes that don't send `tasks` are unchanged.
// ============================================================

function fmtDateLong(dateStr) {
  return new Date(`${dateStr}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}
function fmtWeekday(dateStr) {
  return new Date(`${dateStr}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
}
function fmtChip(dateStr) {
  return new Date(`${dateStr}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}
function fmtDur(mins) {
  if (!mins) return '';
  if (mins >= 60) {
    const h = Math.floor(mins / 60); const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h} hr`;
  }
  return `${mins} min`;
}
// "5551234567" → "(555) 123-4567"; anything else is shown as stored.
function fmtPhone(p) {
  const d = String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(p || '');
}

function mapsUrl(addr) {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr || '')}`;
}

const OUTCOMES = [
  { key: 'completed', label: 'Completed', sub: 'All work done', color: '#16a34a', needsComment: false, showSurvey: true,
    hint: 'Anything worth noting? (optional)' },
  { key: 'partial', label: 'Partially completed', sub: 'Additional work requested', color: '#d97706', needsComment: true, showSurvey: true,
    hint: 'What remains, or what additional work is needed? (required)' },
  { key: 'reschedule', label: 'Did not complete', sub: 'Send this stop back to be rescheduled', color: '#dc2626', needsComment: true, showSurvey: false,
    hint: 'Why couldn\'t the work be done? (required)' },
];
const MAX_PHOTOS = 6;

export default function FieldRoutePage({ config }) {
  const { fetchRoute, submitOutcome: submitOutcomeApi, summaryField, specialsLabel, completeTask } = config;
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [date, setDate] = useState('');
  const [error, setError] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [showLoad, setShowLoad] = useState(null); // null = auto
  const [sheet, setSheet] = useState(null);       // { stop, outcome, comment, done, photos, confirming }
  const [taskSheet, setTaskSheet] = useState(null); // { task, note, photos }
  const [busy, setBusy] = useState(false);
  const idemKey = useRef(null);

  const load = useCallback(async (d) => {
    try {
      const res = await fetchRoute(d ? { date: d } : {});
      setData(res);
      setDate(res.date);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [fetchRoute]);

  useEffect(() => { load(); }, [load]);

  function openSheet(stop) {
    idemKey.current = `${stop.id}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setSheet({ stop, outcome: null, comment: '', done: [], photos: [], confirming: false });
  }
  function toggleDone(id) {
    setSheet(m => ({ ...m, done: m.done.includes(id) ? m.done.filter(x => x !== id) : [...m.done, id] }));
  }
  function addPhotos(files) {
    const list = Array.from(files || []).filter(f => f.type.startsWith('image/'));
    setSheet(m => ({ ...m, photos: [...m.photos, ...list].slice(0, MAX_PHOTOS) }));
  }
  async function submitOutcome() {
    const { stop, outcome, comment, done, photos } = sheet;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('outcome', outcome);
      fd.append('comment', comment.trim());
      fd.append('work_done', JSON.stringify(done));
      fd.append('idempotency_key', idemKey.current);
      photos.forEach(p => fd.append('photos', p));
      await submitOutcomeApi(stop.id, fd);
      setSheet(null);
      setActiveId(null);
      await load(date);
    } catch (e) {
      setSheet(m => ({ ...m, confirming: false }));
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function addTaskPhotos(files) {
    const list = Array.from(files || []).filter(f => f.type.startsWith('image/'));
    setTaskSheet(m => ({ ...m, photos: [...m.photos, ...list].slice(0, MAX_PHOTOS) }));
  }
  async function submitTaskDone() {
    const { task, note, photos } = taskSheet;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('note', note.trim());
      photos.forEach(p => fd.append('photos', p));
      await completeTask(task.id, fd);
      setTaskSheet(null);
      setActiveId(null);
      await load(date);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  const openTaskSheet = (task) => setTaskSheet({ task, note: '', photos: [] });

  // Mirrors the server's validation so the tech finds out before Continue.
  function surveyProblem() {
    if (!sheet?.outcome) return null;
    const o = OUTCOMES.find(x => x.key === sheet.outcome);
    const types = sheet.stop.work_types || [];
    if (o.needsComment && sheet.comment.trim().length < 10) return 'An explanation is required (at least 10 characters).';
    if (sheet.outcome === 'completed' && types.length > 0 && sheet.done.length < types.length) {
      return 'Check off every work item to mark this Completed. If something couldn\'t be done, choose Partially completed.';
    }
    if (sheet.outcome === 'partial' && sheet.done.length === 0) {
      return 'Check off at least one item you completed. If nothing was done, choose Did not complete.';
    }
    return null;
  }

  if (error && !data) return <div className="admin-page"><p className="alert alert-error">{error}</p></div>;
  if (!data) return <div className="admin-page"><p>Loading…</p></div>;

  // Jobs and located tasks share one numbered list (by stop_order).
  const tasks = data.tasks || [];
  const items = [
    ...data.stops.map(s => ({ ...s, key: `j${s.id}` })),
    ...tasks.filter(t => t.has_location).map(t => ({
      ...t, key: `t${t.id}`, is_task: true, account_name: t.title, work_address: t.address,
    })),
  ].sort((a, b) => (a.stop_order ?? 1e9) - (b.stop_order ?? 1e9));
  const shopTasks = tasks.filter(t => !t.has_location).map(t => ({ ...t, key: `t${t.id}`, is_task: true }));
  const isOpen = (s) => (s.is_task ? s.status === 'open' : s.status === 'published');
  const openStops = items.filter(isOpen);
  const doneStops = items.filter(s => !isOpen(s));
  const active = openStops.find(s => s.key === activeId) || openStops[0] || null;
  const total = items.length + shopTasks.length;
  const doneCount = doneStops.length + shopTasks.filter(t => !isOpen(t)).length;
  const allDone = total > 0 && doneCount === total;
  const jobCount = data.stops.length;
  const taskCount = tasks.length;
  const isToday = date === data.today;
  const title = isToday ? 'Today\'s Route' : `${fmtWeekday(date)}'s Route`;
  const supply = data.supply;
  const hasSupply = supply && (supply.items.length > 0 || supply.specials.length > 0);
  const loadOpen = showLoad === null ? doneCount === 0 : showLoad;
  const showChips = data.my_dates.length > 1 || (data.my_dates.length === 1 && data.my_dates[0] !== date);

  return (
    <div className="admin-page">
      <MobilePage
        title={title}
        subtitle={[
          fmtDateLong(date),
          jobCount ? `${jobCount} stop${jobCount === 1 ? '' : 's'}` : null,
          taskCount ? `${taskCount} task${taskCount === 1 ? '' : 's'}` : null,
        ].filter(Boolean).join(' · ')}
        side={total > 0 ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: doneCount === total ? '#16a34a' : '#334155' }}>{doneCount} of {total} done</div>
            <div className="m-progress"><div style={{ width: `${total ? Math.round((doneCount / total) * 100) : 0}%` }} /></div>
          </>
        ) : null}
      >
        {error && <p className="alert alert-error">{error}</p>}

        {showChips && (
          <div className="m-chips">
            {data.my_dates.map(d => (
              <button key={d} type="button" className={`m-chip ${d === date ? 'active' : ''}`} onClick={() => { setActiveId(null); load(d); }}>
                {d === data.today ? 'Today' : fmtChip(d)}
              </button>
            ))}
          </div>
        )}

        {!data.route && (
          <div className="m-card" style={{ textAlign: 'center', padding: 28, color: 'var(--text-muted)' }}>
            No published route for this day.
          </div>
        )}

        {data.route && (
          <div className="m-chips" style={{ marginBottom: 12 }}>
            {hasSupply && (
              <button type="button" className={`m-chip ${loadOpen ? 'active' : ''}`} onClick={() => setShowLoad(!loadOpen)}>
                <Icon name="box" size={18} /> Load list
              </button>
            )}
            {data.shop && items.length > 0 && (
              <button type="button" className={`m-chip ${showMap ? 'active' : ''}`} onClick={() => setShowMap(v => !v)}>
                <Icon name="map" size={18} /> Map
              </button>
            )}
          </div>
        )}

        {hasSupply && loadOpen && (
          <div className="m-card" style={{ marginBottom: 12, background: '#f0f9ff', borderColor: '#bae6fd' }}>
            <div style={{ fontWeight: 800, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="box" size={18} /> Load the truck</div>
            {supply.items.map((it, i) => (
              <div key={i} style={{ fontSize: 15, padding: '3px 0' }}>
                <strong>{it.required}</strong> {it.unit !== 'each' ? `${it.unit} ` : '× '}{it.name}
              </div>
            ))}
            {supply.specials.length > 0 && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #bae6fd' }}>
                <div className="m-section" style={{ padding: '0 0 4px', color: '#92400e' }}>{specialsLabel}</div>
                {supply.specials.map(sp => (
                  <div key={sp.request_id} style={{ fontSize: 14, padding: '2px 0' }}>{sp.property}: {sp.description}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {showMap && data.shop && items.length > 0 && (
          <div style={{ marginBottom: 12, borderRadius: 12, overflow: 'hidden' }}>
            <SignRouteMap shop={data.shop} height={isMobile ? 240 : 320}
              stops={items.map(s => ({ ...s, stop_number: s.stop_order }))} />
          </div>
        )}

        {shopTasks.length > 0 && (
          <>
            <div className="m-section">Shop work</div>
            <div className="m-list">
              {shopTasks.map(t => (
                <StopCard key={t.key} stop={t} summaryField={summaryField} expanded={isOpen(t)} done={!isOpen(t)}
                  actions={isOpen(t) ? (
                    <button type="button" className="m-btn m-btn-primary" onClick={() => openTaskSheet(t)}>
                      <Icon name="check" size={20} color="#fff" /> Mark Done
                    </button>
                  ) : null} />
              ))}
            </div>
          </>
        )}

        {openStops.length > 0 && <div className="m-section">Up next</div>}
        <div className="m-list">
          {openStops.map(s => (
            <StopCard key={s.key} stop={s} summaryField={summaryField}
              expanded={active && active.key === s.key}
              onSelect={() => setActiveId(s.key)}
              actions={!isMobile && active && active.key === s.key ? (
                <div className="m-sticky-inline">
                  <a className="m-btn-icon" href={mapsUrl(s.work_address)} target="_blank" rel="noreferrer" aria-label="Directions"><Icon name="nav" size={22} /></a>
                  {s.is_task
                    ? <button type="button" className="m-btn m-btn-primary" onClick={() => openTaskSheet(s)}>Mark Done</button>
                    : <button type="button" className="m-btn m-btn-primary" onClick={() => openSheet(s)}>Update Work Status</button>}
                </div>
              ) : null} />
          ))}
        </div>

        {data.route && allDone && (
          <div className="m-card" style={{ textAlign: 'center', padding: 24, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}><span className="m-badge done"><Icon name="check" size={18} strokeWidth={3} /></span></div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>All stops done</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Nice work. Nothing left on this route.</div>
          </div>
        )}

        {doneStops.length > 0 && <div className="m-section" style={{ marginTop: 6 }}>Done</div>}
        <div className="m-list">
          {doneStops.map(s => <StopCard key={s.key} stop={s} summaryField={summaryField} done />)}
        </div>
      </MobilePage>

      {/* Pinned primary action — the active stop's directions + status */}
      {isMobile && active && (
        <StickyAction>
          <a className="m-btn-icon" href={mapsUrl(active.work_address)} target="_blank" rel="noreferrer" aria-label="Directions">
            <Icon name="nav" size={22} />
          </a>
          {active.is_task
            ? <button type="button" className="m-btn m-btn-primary" onClick={() => openTaskSheet(active)}>Mark Done</button>
            : <button type="button" className="m-btn m-btn-primary" onClick={() => openSheet(active)}>Update Work Status</button>}
        </StickyAction>
      )}

      {/* Task done — one step: optional note + photos, then Done */}
      <BottomSheet
        open={!!taskSheet}
        title={taskSheet ? `🛠 ${taskSheet.task.title}` : 'Task'}
        subtitle={taskSheet ? `Task · ${fmtDur(taskSheet.task.estimated_minutes)}${taskSheet.task.address ? ` · ${taskSheet.task.address}` : ''}` : ''}
        onClose={() => setTaskSheet(null)}
        closeDisabled={busy}
        footer={taskSheet && (
          <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={submitTaskDone}>
            {busy ? 'Saving…' : 'Mark Done'}
          </button>
        )}
      >
        {taskSheet && (
          <>
            {taskSheet.task.notes && (
              <div className="m-card" style={{ marginBottom: 8, fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{taskSheet.task.notes}</div>
            )}
            <div className="m-section">Notes</div>
            <textarea className="m-textarea" rows={3} placeholder="Anything worth noting? (optional)"
              value={taskSheet.note} onChange={e => setTaskSheet(m => ({ ...m, note: e.target.value }))} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
              {taskSheet.photos.length < MAX_PHOTOS && (
                <label className="m-photo-add">
                  <Icon name="camera" size={20} /> {taskSheet.photos.length ? 'Add more' : 'Add photos'}
                  <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                    onChange={e => { addTaskPhotos(e.target.files); e.target.value = ''; }} />
                </label>
              )}
              {taskSheet.photos.map((p, i) => (
                <div key={i} className="m-photo-thumb">
                  <img src={URL.createObjectURL(p)} alt={`upload ${i + 1}`} />
                  <button type="button" aria-label="Remove photo" onClick={() => setTaskSheet(m => ({ ...m, photos: m.photos.filter((_, x) => x !== i) }))}>
                    <Icon name="x" size={12} strokeWidth={3} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </BottomSheet>

      {/* Outcome flow — bottom sheet, submit pinned in the footer */}
      <BottomSheet
        open={!!sheet}
        title={sheet?.stop.account_name || 'Stop'}
        subtitle={sheet ? `Stop ${sheet.stop.stop_order} · ${sheet.stop[summaryField] || ''}` : ''}
        onClose={() => setSheet(null)}
        closeDisabled={busy}
        footer={sheet && (!sheet.confirming ? (
          <button type="button" className="m-btn m-btn-primary" disabled={busy || !sheet.outcome || !!surveyProblem()}
            onClick={() => setSheet(m => ({ ...m, confirming: true }))}>
            Continue
          </button>
        ) : (
          <>
            <button type="button" className="m-btn m-btn-secondary" disabled={busy} onClick={() => setSheet(m => ({ ...m, confirming: false }))}>Back</button>
            <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={submitOutcome}>
              {busy ? 'Saving…' : (OUTCOMES.find(o => o.key === sheet.outcome)?.key === 'completed' ? 'Mark Completed' : 'Confirm')}
            </button>
          </>
        ))}
      >
        {sheet && !sheet.confirming && (
          <>
            <div className="m-section">Outcome</div>
            {OUTCOMES.map(o => {
              const sel = sheet.outcome === o.key;
              return (
                <button key={o.key} type="button" className={`m-choice ${sel ? 'selected' : ''}`}
                  style={sel ? { borderColor: o.color, background: `${o.color}0f` } : {}}
                  onClick={() => setSheet(m => ({ ...m, outcome: o.key }))}>
                  <span className="m-choice-dot" style={sel ? { background: o.color } : {}}>
                    {sel && <Icon name="check" size={14} color="#fff" strokeWidth={3} />}
                  </span>
                  <span>
                    <div className="m-choice-label">{o.label}</div>
                    <div className="m-choice-sub">{o.sub}</div>
                  </span>
                </button>
              );
            })}

            {sheet.outcome && OUTCOMES.find(o => o.key === sheet.outcome).showSurvey && (sheet.stop.work_types || []).length > 0 && (
              <>
                <div className="m-section" style={{ paddingTop: 12 }}>What did you do?</div>
                {(sheet.stop.work_types || []).map(wt => {
                  const on = sheet.done.includes(wt.id);
                  return (
                    <div key={wt.id} role="checkbox" aria-checked={on} tabIndex={0}
                      className={`m-check ${on ? 'on' : ''}`} onClick={() => toggleDone(wt.id)}
                      onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleDone(wt.id); } }}>
                      <span className="m-check-box">{on && <Icon name="check" size={16} color="#fff" strokeWidth={3} />}</span>
                      <span>{wt.name}</span>
                    </div>
                  );
                })}
              </>
            )}

            {sheet.outcome && (
              <>
                <div className="m-section" style={{ paddingTop: 12 }}>Notes</div>
                <textarea className="m-textarea" rows={3}
                  placeholder={OUTCOMES.find(o => o.key === sheet.outcome).hint}
                  value={sheet.comment}
                  onChange={e => setSheet(m => ({ ...m, comment: e.target.value }))} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                  {sheet.photos.length < MAX_PHOTOS && (
                    <label className="m-photo-add">
                      <Icon name="camera" size={20} /> {sheet.photos.length ? 'Add more' : 'Add photos'}
                      <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                        onChange={e => { addPhotos(e.target.files); e.target.value = ''; }} />
                    </label>
                  )}
                  {sheet.photos.map((p, i) => (
                    <div key={i} className="m-photo-thumb">
                      <img src={URL.createObjectURL(p)} alt={`upload ${i + 1}`} />
                      <button type="button" aria-label="Remove photo" onClick={() => setSheet(m => ({ ...m, photos: m.photos.filter((_, x) => x !== i) }))}>
                        <Icon name="x" size={12} strokeWidth={3} />
                      </button>
                    </div>
                  ))}
                </div>
                {surveyProblem() && <div className="m-hint">{surveyProblem()}</div>}
              </>
            )}
          </>
        )}

        {sheet && sheet.confirming && (
          <div className="m-card" style={{ marginTop: 4 }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, color: OUTCOMES.find(o => o.key === sheet.outcome).color }}>
              {OUTCOMES.find(o => o.key === sheet.outcome).label}
            </div>
            <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.45 }}>
              {sheet.outcome === 'completed'
                ? 'This stop will be marked complete and the submitter will be texted.'
                : sheet.outcome === 'partial'
                  ? 'The finished items are recorded. The remaining work goes back to the scheduler and the submitter will be texted.'
                  : 'This work returns to the scheduler to be re-planned, and the submitter will be texted.'}
            </div>
            {sheet.done.length > 0 && (
              <div style={{ fontSize: 14, color: '#065f46', marginTop: 10 }}>
                {(sheet.stop.work_types || []).filter(wt => sheet.done.includes(wt.id)).map(wt => wt.name).join(' · ')}
              </div>
            )}
            {sheet.photos.length > 0 && (
              <div style={{ fontSize: 13, color: '#334155', marginTop: 6 }}>{sheet.photos.length} photo{sheet.photos.length > 1 ? 's' : ''} attached</div>
            )}
            {sheet.comment.trim() && (
              <div style={{ fontSize: 14, color: '#0f172a', marginTop: 8, fontStyle: 'italic' }}>“{sheet.comment.trim()}”</div>
            )}
          </div>
        )}
      </BottomSheet>
    </div>
  );
}

function StopCard({ stop: s, summaryField, expanded, done, onSelect, actions }) {
  let photos = [];
  try { photos = JSON.parse(s.photo_urls || '[]'); } catch { /* none */ }
  const cls = `m-card ${expanded ? 'current' : ''} ${done ? 'done' : ''}`;
  const clickable = !done && !expanded;
  return (
    <div className={cls} role={clickable ? 'button' : undefined} tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onSelect : undefined}
      onKeyDown={clickable ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } } : undefined}
      style={clickable ? { cursor: 'pointer' } : undefined}>
      <div className="m-card-row">
        <div className={`m-badge ${done ? 'done' : ''}`} style={!done && s.is_task ? { background: '#0d9488', color: '#fff' } : undefined}>
          {done ? <Icon name="check" size={18} strokeWidth={3} /> : s.is_task && !s.has_location ? '🛠' : s.stop_order}
        </div>
        <div className="m-card-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div className="m-card-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: expanded ? 'normal' : 'nowrap' }}>
              {s.is_task ? (s.has_location ? `🛠 ${s.title}` : s.title) : (s.account_name || '—')}
            </div>
            {s.is_emergency ? <span className="m-pill" style={{ background: '#ef4444', color: '#fff' }}>EMERGENCY</span>
              : (clickable ? <Icon name="chevron" size={18} color="#94a3b8" /> : null)}
          </div>
          <div className="m-card-meta">{s.is_task ? (s.address || 'At the shop') : (s.work_address || 'No address on file')}</div>
          <div className="m-card-accent">{s.is_task ? 'Task' : s[summaryField]}{s.estimated_minutes ? ` · ${fmtDur(s.estimated_minutes)}` : ''}</div>
          {done && <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', marginTop: 2 }}>{s.is_task ? 'Done' : s.status === 'completed' ? 'Completed' : s.status}</div>}
          {expanded && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              {s.notes && <div style={{ fontSize: 14, color: '#0f172a', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{s.notes}</div>}
              {s.access_instructions && (
                <div className="m-note"><Icon name="key" size={16} color="#a16207" style={{ flexShrink: 0, marginTop: 1 }} /><div><strong>Access:</strong> {s.access_instructions}</div></div>
              )}
              {/* Who asked for this work — one tap to call them with questions (Matt, Oct 7). */}
              {!s.is_task && s.submitter_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, background: '#f8fafc', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-faint)' }}>Submitted by</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{s.submitter_name}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{s.submitter_phone ? fmtPhone(s.submitter_phone) : 'No phone on file'}</div>
                  </div>
                  {s.submitter_phone && (
                    <a className="m-btn-icon" href={`tel:${String(s.submitter_phone).replace(/[^0-9+]/g, '')}`}
                      aria-label={`Call ${s.submitter_name}`} onClick={e => e.stopPropagation()}>
                      <Icon name="phone" size={22} color="#16a34a" />
                    </a>
                  )}
                </div>
              )}
              {photos.length > 0 && (
                <div className="m-photos">
                  {photos.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt={`photo ${i + 1}`} /></a>)}
                </div>
              )}
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
